import { Auth0Client, GetTokenSilentlyOptions } from '@auth0/auth0-spa-js'
import { pipe } from '@helo/utils'
import axios from 'axios'
import { DocumentNode } from 'graphql'
import { jwtDecode } from 'jwt-decode'

import { Role } from '../../../config/roles'
import { currentUserQuery } from '../../../queries/graphql/graphqlQueries'
import { apolloClient } from '../../../services/apolloClient'
import type { ConfigSlice } from '../config/configSlice'
import { User } from './AuthSlice.types'
import { AuthSlice } from './authSlice'

export class TokenError {
  readonly _tag = 'TokenError'
  constructor(public message: string) {}
}

export const NO_ORGANIZATION_ORGANIZATION = {
  display_name: 'No Organization',
  name: 'noorganization',
  id: 'noorganization',
}

const createAuth0Client =
  () =>
  <T extends AuthSlice & ConfigSlice>(store: T): T => ({
    ...store,
    auth: {
      ...store.auth,
      auth0Client: new Auth0Client({
        domain: store.config.AUTH0_DOMAIN,
        clientId: store.config.AUTH0_CLIENTID,
        cacheLocation: 'localstorage',
        authorizationParams: {
          audience: store.config.AUTH_AUDIENCE,
          redirectUri: window?.location?.origin,
          responseType: 'token',
          scope: 'openid profile email',
        },
      }),
    },
  })

const shouldSkipAuthSetup =
  () =>
  <T extends AuthSlice>(store: T): boolean => {
    return store.auth.isAuthenticated || store.auth.initializing
  }

const setInitializing =
  () =>
  <T extends AuthSlice>(store: T): T => ({
    ...store,
    auth: {
      ...store.auth,
      initialized: false,
      initializing: true,
      status: 'initializing',
    },
  })

/**
 * Provides the returnTo, but also needs to be called before getting the token
 * as this function sets up auth0 after the user has logged in.
 */
const handleAuth0RedirectCallback =
  () =>
  async <T extends AuthSlice>(store: T): Promise<T> => {
    const client = store.auth.auth0Client
    if (!client) {
      throw new Error('Auth0 client not initialized')
    }
    const result = await client
      .handleRedirectCallback<{
        returnTo?: string
      }>()
      .catch(error => {
        return null
      }) // don't care about this error.

    return {
      ...store,
      auth: {
        ...store.auth,
        // An error is thrown if a refresh occurs but the user is already logged in.
        // this means that null, set if error above, means the user is logged in already.
        isLoggingIn: result === null ? false : true,
        redirectTo: result?.appState?.returnTo ?? null,
      },
    }
  }

/**
 * Retrieve the auth0 token if logged in.
 */
const getAuth0Token =
  (options: GetTokenSilentlyOptions = {}) =>
  async <T extends AuthSlice>(store: T): Promise<T> => {
    const client = store.auth.auth0Client
    if (!client) {
      throw new Error('Auth0 client not initialized')
    }
    const result = await client
      .getTokenSilently(options)
      .then(token => token)
      .catch(error => {
        throw new TokenError(error.message)
      })

    return {
      ...store,
      auth: {
        ...store.auth,
        token: result,
      },
    }
  }

/**
 * Forwards the user to the Auth0 login page.
 * Saves the current url for redirecting after login.
 */
const loginWithRedirect =
  ({ returnTo }: { returnTo?: string }) =>
  async <T extends AuthSlice>(store: T): Promise<T> => {
    const client = store.auth.auth0Client
    if (!client) {
      throw new Error('Auth0 client not initialized')
    }
    await client.loginWithRedirect({
      authorizationParams: { redirect_uri: window.location.origin },
      appState: { returnTo },
    })
    return store
  }

/**
 * Sets axios default headers.
 *
 * Must be called immediately after logging in to prevent
 * requests going out before this is set.
 */
const setAxiosHeaders = (headers: Record<string, any>) => {
  axios.defaults.headers.common = {
    ...axios.defaults.headers.common,
    ...(headers as any),
  }
}

const addTokenToHeaders =
  () =>
  <T extends AuthSlice>(store: T): T => {
    setAxiosHeaders({
      Authorization: store.auth.token ? `Bearer ${store.auth.token}` : '',
      'Access-Control-Allow-Credentials': true,
    })
    return store
  }

const getAuth0User =
  () =>
  async <T extends AuthSlice>(store: T): Promise<T> => {
    const client = store.auth.auth0Client
    if (!client) {
      throw new Error('Auth0 client not initialized')
    }
    const user = await client.getUser().catch(error => {
      throw new TokenError(error.message)
    })

    return {
      ...store,
      auth: {
        ...store.auth,
        auth0Profile: user ?? null,
      },
    }
  }

const setupUser =
  () =>
  <T extends AuthSlice>(store: T): T => {
    const { auth0Profile } = store.auth
    if (!auth0Profile)
      return {
        ...store,
        auth: {
          ...store.auth,
          user: null,
        },
      }

    const roles = auth0Profile?.['http://helo.app.com/claims/role'] ?? []
    const organizations = auth0Profile?.['http://helo.app.com/claims/org'] ?? []

    //Backend is not properly sending first and last name seperately.
    //this is hack to get them from the full name they are sending.
    const splitName = auth0Profile.name?.split(' ') || ''
    const firstName = splitName[0] || auth0Profile.name
    const lastName = splitName[1] || auth0Profile.name

    const user = {
      ...auth0Profile,
      // TODO: move into followup action
      organization:
        organizations.filter(org => org.name !== 'allcustomers')?.[0] ??
        NO_ORGANIZATION_ORGANIZATION,
      firstName,
      lastName,
      roles,
    }

    return {
      ...store,
      auth: {
        ...store.auth,
        user,
      },
    }
  }

const getCurrentUser =
  () =>
  async <T extends AuthSlice>(store: T): Promise<T> => {
    // Only fetch if we have a token
    if (!store.auth.token) {
      console.warn('No token available, skipping current user fetch')
      return store
    }

    try {
      const result = await apolloClient.query({
        query: currentUserQuery as DocumentNode,
        fetchPolicy: 'network-only',
        errorPolicy: 'all', // Continue even if there are errors
      })

      const dbUser = (result.data as any)?.currentUser
      if (!dbUser) {
        console.warn('No current user data returned from query')
        return store
      }

      // Convert database user to match the User type structure
      const roles = (dbUser.roles as Role[]) ?? []
      const organizations =
        dbUser.organizations?.map((org: any) => ({
          display_name: org.displayName,
          id: org.id,
          name: org.name,
        })) ?? []

      // Split name if available
      const splitName = dbUser.name?.split(' ') || ''
      const firstName = splitName[0] || dbUser.name || ''
      const lastName = splitName[1] || dbUser.name || ''

      const user = {
        sub: dbUser.id, // Keep sub for compatibility
        email: dbUser.email,
        name: dbUser.name || dbUser.email,
        nickname: dbUser.nickname || dbUser.email,
        firstName,
        lastName,
        roles,
        organizations,
        organization:
          organizations.filter(
            (org: any) => org.name !== 'allcustomers',
          )?.[0] ?? NO_ORGANIZATION_ORGANIZATION,
        // Store the database user ID for easy access
        id: dbUser.id,
        // Auth0 claims for compatibility
        ['http://helo.app.com/claims/role']: roles,
        ['http://helo.app.com/claims/org']: organizations,
      } as User

      return {
        ...store,
        auth: {
          ...store.auth,
          user,
        },
      }
    } catch (error) {
      console.error('Error fetching current user:', error)
      if (error instanceof Error) {
        console.error('Error message:', error.message)
        console.error('Error stack:', error.stack)
      }
      // Log GraphQL errors if present
      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        console.error('GraphQL errors:', (error as any).graphQLErrors)
      }
      if (error && typeof error === 'object' && 'networkError' in error) {
        console.error('Network error:', (error as any).networkError)
      }
      // Return state unchanged on error - don't break auth flow
      return store
    }
  }

const setupExpirations =
  () =>
  <T extends AuthSlice>(store: T): T => {
    return pipe(
      store,
      (store: T) => {
        if (!store.auth.token) {
          return pipe.return(store)
        }

        const decodedToken = jwtDecode(store.auth.token!)

        const issuedAt = decodedToken?.iat
          ? new Date(decodedToken.iat * 1000).getTime()
          : null

        const expiresAt = decodedToken?.exp
          ? new Date(decodedToken.exp * 1000).getTime()
          : null

        // this shouldn't ever really be the case...
        if (!issuedAt || !expiresAt) {
          return pipe.return(store)
        }
        return {
          ...store,
          auth: {
            ...store.auth,
            tokenIssuedAt: issuedAt,
            tokenExpiresAt: expiresAt,
          },
        }
      },
      (store: T): T => {
        // Always clear since they're being redefiened.
        if (store.auth.inactiveTimer) clearTimeout(store.auth.inactiveTimer)
        if (store.auth.expirationTimer) clearTimeout(store.auth.expirationTimer)

        const now = new Date().getTime()
        const timeSinceIssued = now - store.auth.tokenIssuedAt!
        // This is a value from Auth0's settings. May need to change.
        const inactivityTimeoutMs = 120 * 60 * 1000
        // Get a new token to prevent innactivity if this timer still running.
        // this running signals that the tab is still open.
        const inactivityEarlyResetMs = 30 * 1000
        // Give a second or so to ensure that expiration has occurred.
        const expirationDelayMs = 1000
        const timeToInactive = inactivityTimeoutMs - timeSinceIssued

        const timetoExpire = store.auth.tokenExpiresAt! - now

        // TODO: replace with call to redirect to login.
        if (timeToInactive < 0 || timetoExpire < 0) {
          console.log('setupExpirations expired on setup')
          // useAuthStore.getState().initialize(true)
          loginWithRedirect({
            returnTo: `${window.location.pathname}${window.location.search}`,
          })(store)
          return pipe.return(store) as unknown as T
        }

        const inactiveTimeout = setTimeout(() => {
          console.log('reached inactivity timeout')

          // TODO: should replace with attempt to get new token with  cacheMode: reset ? 'off' : undefined,
          loginWithRedirect({
            returnTo: `${window.location.pathname}${window.location.search}`,
          })(store)
        }, timeToInactive - inactivityEarlyResetMs)

        const expirationTimeout = setTimeout(() => {
          console.log('reached expiration timeout')

          // TODO: should replace with attempt to get new token with  cacheMode: reset ? 'off' : undefined,
          loginWithRedirect({
            returnTo: `${window.location.pathname}${window.location.search}`,
          })(store)
        }, timetoExpire + expirationDelayMs)

        return {
          ...store,
          auth: {
            ...store.auth,
            inactiveTimer: inactiveTimeout,
            expirationTimer: expirationTimeout,
          },
        }
      },
    ) as unknown as T
  }

const userHasRoles =
  (allowedRoles: Role[] = []) =>
  <T extends AuthSlice>(store: T) =>
    allowedRoles.length === 0 ||
    allowedRoles.some(role => store.auth.user?.roles.includes(role))

const setIsAuthenticated =
  () =>
  <T extends AuthSlice>(store: T): T => ({
    ...store,
    auth: {
      ...store.auth,
      isAuthenticated: true,
    },
  })

export const AuthSliceParts = {
  createAuth0Client,
  shouldSkipAuthSetup,
  setInitializing,
  handleAuth0RedirectCallback,
  getAuth0Token,
  loginWithRedirect,
  addTokenToHeaders,
  getAuth0User,
  setupUser,
  getCurrentUser,
  setupExpirations,
  userHasRoles,
  setIsAuthenticated,
}
